import { ref } from 'vue'
import { z } from 'zod'

import programsStore from '@/features/programs/stores/programs/programs.store'

/**
 * État de session de connexion partagé entre la page de login et la modale MFA
 * (KC-439, Volets B & C). Implémenté en singleton module-level pour que les deux
 * composants voient le même état.
 *
 * `loginStartTimestamp` et `initialDeepLink` sont aussi copiés en `sessionStorage`
 * afin de survivre au round-trip SSO (l'app reboote sur `/?token=…`).
 *
 * Aucune PII n'est exposée : on ne stocke jamais le mot de passe, et l'email
 * n'est gardé en mémoire que pour détecter un changement entre deux tentatives.
 */

const LOGIN_START_TS_KEY = 'auth:login_start_ts'
const INITIAL_DEEP_LINK_KEY = 'auth:initial_deep_link'

export type LoginAuthMethod = 'email' | 'email_mfa' | 'sso'
export type FieldChanged = 'email' | 'password' | 'both' | 'none' | null
type InitialDeepLink = { programId: string; tab: string | null }

// Singletons partagés entre login/page.vue et la modale MFA
const loginStartTimestamp = ref<number>(0)
const attemptCount = ref(0)
const lastSubmittedEmail = ref<string | null>(null)
const passwordChangedSinceLastAttempt = ref(false)
const usedPasswordReset = ref(false)
const mfaAttemptCount = ref(0)
const initialDeepLink = ref<InitialDeepLink | null>(null)

function readInitialDeepLinkFromUrl(): InitialDeepLink | null {
  try {
    const params = new URLSearchParams(window.location.search)
    const programId = z.string().uuid().safeParse(params.get('programId')).data
    if (!programId) return null
    return { programId, tab: params.get('tab') }
  } catch {
    return null
  }
}

function persist() {
  try {
    if (loginStartTimestamp.value) {
      sessionStorage.setItem(LOGIN_START_TS_KEY, String(loginStartTimestamp.value))
    }
    if (initialDeepLink.value) {
      sessionStorage.setItem(INITIAL_DEEP_LINK_KEY, JSON.stringify(initialDeepLink.value))
    }
  } catch {
    // sessionStorage indisponible : on ignore (timings best-effort)
  }
}

function restore() {
  try {
    if (!loginStartTimestamp.value) {
      const ts = Number(sessionStorage.getItem(LOGIN_START_TS_KEY))
      if (ts) loginStartTimestamp.value = ts
    }
    if (!initialDeepLink.value) {
      const raw = sessionStorage.getItem(INITIAL_DEEP_LINK_KEY)
      if (raw) initialDeepLink.value = JSON.parse(raw) as InitialDeepLink
    }
  } catch {
    // valeur absente ou corrompue : on ignore
  }
}

export default function useLoginSession() {
  /**
   * À appeler au montage de la page de login. Repart d'un état propre puis
   * initialise le timing et le deep-link.
   *
   * Les compteurs et le timing sont des singletons module-level : sans ce reset,
   * ils survivraient à une tentative abandonnée ou à un changement d'utilisateur
   * dans le même onglet et fausseraient les events suivants (KC-439). Le retour
   * SSO ne passe pas par `start()` mais par `restore()` (global-layout), donc la
   * persistance du round-trip n'est pas affectée.
   */
  function start() {
    reset()
    loginStartTimestamp.value = Date.now()
    initialDeepLink.value = readInitialDeepLinkFromUrl()
    persist()
  }

  function timeSinceStart(): number {
    if (!loginStartTimestamp.value) return 0
    return Date.now() - loginStartTimestamp.value
  }

  /** Enregistre une tentative de login et calcule ce qui a changé depuis la précédente. */
  function registerAttempt(email: string): { attemptNumber: number; fieldChanged: FieldChanged } {
    const isFirst = attemptCount.value === 0
    let fieldChanged: FieldChanged = null
    if (!isFirst) {
      const emailChanged = email !== lastSubmittedEmail.value
      const passwordChanged = passwordChangedSinceLastAttempt.value
      if (emailChanged && passwordChanged) fieldChanged = 'both'
      else if (emailChanged) fieldChanged = 'email'
      else if (passwordChanged) fieldChanged = 'password'
      else fieldChanged = 'none'
    }
    attemptCount.value += 1
    lastSubmittedEmail.value = email
    passwordChangedSinceLastAttempt.value = false
    return { attemptNumber: attemptCount.value, fieldChanged }
  }

  function markPasswordChanged() {
    passwordChangedSinceLastAttempt.value = true
  }

  function markUsedPasswordReset() {
    usedPasswordReset.value = true
  }

  /** Incrémente et retourne le numéro de tentative MFA. */
  function registerMfaAttempt(): number {
    mfaAttemptCount.value += 1
    return mfaAttemptCount.value
  }

  function hadProgramDeepLink(): boolean {
    return initialDeepLink.value !== null
  }

  /**
   * Volet C : le programId du deep-link initial est-il encore le programme
   * sélectionné après l'auth ? `undefined` s'il n'y avait pas de deep-link.
   * Avant KC-501 (restitution), on s'attend à `false` sur la quasi-totalité
   * des logins post-deep-link — c'est la baseline.
   */
  function programContextPreserved(): boolean | undefined {
    if (!initialDeepLink.value) return undefined
    return initialDeepLink.value.programId === programsStore.selectedProgramId
  }

  /** Purge l'état (après un login_complete réussi). */
  function reset() {
    loginStartTimestamp.value = 0
    attemptCount.value = 0
    lastSubmittedEmail.value = null
    passwordChangedSinceLastAttempt.value = false
    usedPasswordReset.value = false
    mfaAttemptCount.value = 0
    initialDeepLink.value = null
    try {
      sessionStorage.removeItem(LOGIN_START_TS_KEY)
      sessionStorage.removeItem(INITIAL_DEEP_LINK_KEY)
    } catch {
      // ignore
    }
  }

  return {
    // état (lecture)
    attemptCount,
    usedPasswordReset,
    mfaAttemptCount,
    initialDeepLink,
    // actions
    start,
    restore,
    timeSinceStart,
    registerAttempt,
    markPasswordChanged,
    markUsedPasswordReset,
    registerMfaAttempt,
    hadProgramDeepLink,
    programContextPreserved,
    reset,
  }
}
